<template>
  <b-modal
    :active="true"
    has-modal-card
    trap-focus
    :can-cancel="false"
    append-to-body
    custom-class="notifications-modal-wrapper"
  >
    <div class="modal-card notifications-modal-card">
      <header class="modal-card-head notifications-modal-head">
        <p class="modal-card-title">
          {{ $t('notifications') }} - {{ selectedSectionLabel }}
        </p>
        <button
          type="button"
          class="delete notifications-modal-close"
          @click="handleClose"
          aria-label="close"
        ></button>
      </header>

      <section class="modal-card-body">
        <b-tabs
          v-model="activeTab"
          expanded
          animated
          class="notifications-tabs"
        >
          <b-tab-item :label="$t('notifications_modal.watchers')">
            <watchers-tab
              :watchers="watchers"
              :users="watcherCandidateUsers"
              :watcher-search.sync="watcherSearch"
              :can-manage-watchers="canManageWatchers"
              :current-user="currentUser"
              @add-watcher="addWatcher"
              @toggle-current-user="toggleCurrentUserWatcher"
              @remove-watcher="removeWatcher"
            />
          </b-tab-item>

          <b-tab-item :label="$t('notifications_modal.send_notification')">
            <send-notification-tab
              :current-job="currentJob"
              :watchers="watchers"
              :users="watcherCandidateUsers"
              :sites="sites"
              :notification-recipients.sync="notificationRecipients"
              :notification-message.sync="notificationMessage"
              :templates="notificationTemplates"
              :selected-template-id.sync="selectedTemplateId"
              :is-loading-templates="isLoadingTemplates"
              :excluded-recipients.sync="excludedRecipients"
              :specific-recipients.sync="specificRecipients"
            />
          </b-tab-item>

          <b-tab-item :label="$t('notifications_modal.notification_log')">
            <notification-log-tab
              :notification-logs="notificationLogs"
              :is-loading-notification-log="isLoadingNotificationLog"
              :users="users"
            />
          </b-tab-item>
        </b-tabs>
      </section>

      <footer class="modal-card-foot">
        <b-button class="notifications-btn-close" @click="handleClose">{{
          $t('close')
        }}</b-button>
        <b-button
          class="notifications-btn-primary"
          :loading="isSending"
          @click="sendNotification"
          v-if="activeTab === 1"
          :disabled="!hasRecipientsSelected"
        >
          {{ $t('notifications_modal.send') }}
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import { USERS_GET_USERS } from '../../store/users/actionTypes';
import { GET_USERS } from '../../store/users/getters/getterTypes';
import {
  GET_JOB_NOTIFICATION_LOG,
  GET_NOTIFICATION_TEMPLATES,
  SEND_JOB_NOTIFICATION,
  UPDATE_JOB_WATCHERS,
} from '../../store/notifications/actions/actionTypes';
import WatchersTab from './NotificationsModalComponents/WatchersTab';
import SendNotificationTab from './NotificationsModalComponents/SendNotificationTab';
import NotificationLogTab from './NotificationsModalComponents/NotificationLogTab';

export default {
  name: 'notifications-modal',
  components: {
    WatchersTab,
    SendNotificationTab,
    NotificationLogTab,
  },
  props: {
    job: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeTab: 0,
      watchers: [],
      watcherSearch: '',
      notificationRecipients: {
        watchers: false,
        csm: false,
        repairEngineer: false,
        supervisor: false,
        projectOwner: false,
      },
      notificationMessage: '',
      isSaving: false,
      isSending: false,
      notificationTemplates: [],
      selectedTemplateId: '',
      isLoadingTemplates: false,
      notificationLogs: [],
      isLoadingNotificationLog: false,
      hasLoadedNotificationLog: false,
      excludedRecipients: [],
      specificRecipients: [],
      watcherSaveTimer: null,
      watcherLastSavedSignature: '',
      watcherPersistPromise: null,
    };
  },
  computed: {
    currentJob() {
      return this.job;
    },
    currentUser() {
      return this.$store.state.currentUser || null;
    },
    isPowerAdmin() {
      return parseInt(this.currentUser?.privil, 10) === 1;
    },
    isJobOwner() {
      return this.isSameUser(
        this.currentJob?.prodCreatedByUser,
        this.currentUser
      );
    },
    canManageWatchers() {
      return this.isPowerAdmin || this.isJobOwner;
    },
    selectedSectionLabel() {
      if (this.activeTab === 0) {
        return this.$t('notifications_modal.watchers');
      }
      if (this.activeTab === 1) {
        return this.$t('notifications_modal.send_notification');
      }
      return this.$t('notifications_modal.notification_log');
    },
    users() {
      return this.$store.getters[GET_USERS] || [];
    },
    jobLocationIds() {
      const locationIds = new Set();
      const primarySiteId = this.normalizeSiteId(
        this.currentJob?.executingSite
      );

      if (primarySiteId) {
        locationIds.add(primarySiteId);
      }

      const secondarySites = Array.isArray(this.currentJob?.sites)
        ? this.currentJob.sites
        : [];

      secondarySites.forEach((site) => {
        const siteId = this.normalizeSiteId(site);
        if (siteId) {
          locationIds.add(siteId);
        }
      });

      return locationIds;
    },
    watcherCandidateUsers() {
      if (!Array.isArray(this.users) || this.users.length === 0) {
        return [];
      }

      return this.users.filter(
        (user) =>
          this.userHasGlobalSeeAllJobsPermission(user) ||
          this.userBelongsToJobLocations(user)
      );
    },
    csmUsers() {
      if (!this.currentJob || !Array.isArray(this.currentJob.csmUser)) {
        return [];
      }

      return this.currentJob.csmUser.map((csm) => ({
        _id: csm.id || csm._id,
        fullname: csm.name || csm.fullname,
      }));
    },
    repairEngineerUsers() {
      if (
        !this.currentJob ||
        !Array.isArray(this.currentJob.repairEngineerUser)
      ) {
        return [];
      }

      return this.currentJob.repairEngineerUser.map((re) => ({
        _id: re.id || re._id,
        fullname: re.name || re.fullname,
      }));
    },
    supervisorUsers() {
      const usersFromJobLocations = Array.isArray(this.users) ? this.users : [];
      const currentJobSiteId = this.normalizeSiteId(this.getCurrentJobSiteId());
      const supervisorsFromLocations = usersFromJobLocations
        .filter(
          (user) =>
            this.isSupervisorRole(user) &&
            this.isPrimaryLocationMatch(user, currentJobSiteId)
        )
        .map((user) => ({
          _id: user._id || user.id,
          fullname: user.fullname || user.name || user._id || user.id,
        }))
        .filter((user) => user && user._id);

      if (usersFromJobLocations.length > 0) {
        return supervisorsFromLocations;
      }

      if (!this.currentJob || !Array.isArray(this.currentJob.supervisorUser)) {
        return [];
      }

      return this.currentJob.supervisorUser
        .map((sup) => ({
          _id: sup.id || sup._id,
          fullname: sup.name || sup.fullname,
        }))
        .filter((sup) => sup && sup._id);
    },
    projectOwnerUsers() {
      if (!this.currentJob) {
        return [];
      }

      if (Array.isArray(this.currentJob.projectOwnerUser)) {
        return this.currentJob.projectOwnerUser
          .map((projectOwner) => ({
            _id: projectOwner.id || projectOwner._id,
            fullname: projectOwner.name || projectOwner.fullname,
          }))
          .filter((projectOwner) => projectOwner && projectOwner._id);
      }

      if (this.currentJob.prodCreatedByUser) {
        const owner = this.currentJob.prodCreatedByUser;
        const ownerId = owner._id || owner.email || owner.id;
        if (!ownerId) {
          return [];
        }

        return [
          {
            _id: ownerId,
            fullname: owner.fullname || owner.name || ownerId,
          },
        ];
      }

      return [];
    },
    sites() {
      return this.$store.state.sites || [];
    },
    currentSite() {
      const jobSiteId = this.getCurrentJobSiteId();

      if (!jobSiteId) {
        return null;
      }

      return this.sites.find((site) => site._id === jobSiteId) || null;
    },
    isCsmAutoIncluded() {
      return this.currentSite?.csmNotificationsEnabled === true;
    },
    isRepairEngineerAutoIncluded() {
      return this.currentSite?.repairEngineerNotificationsEnabled === true;
    },
    isSupervisorAutoIncluded() {
      return this.currentSite?.supervisorNotificationsEnabled === true;
    },
    isProjectOwnerAutoIncluded() {
      return this.currentSite?.projectOwnerNotificationsEnabled === true;
    },
    selectedNotificationRecipients() {
      const recipients = [];

      if (this.notificationRecipients.watchers) {
        recipients.push(...this.watchers);
      }

      if (this.notificationRecipients.csm) {
        recipients.push(...this.csmUsers);
      }

      if (this.notificationRecipients.repairEngineer) {
        recipients.push(...this.repairEngineerUsers);
      }

      if (this.notificationRecipients.supervisor) {
        recipients.push(...this.supervisorUsers);
      }

      if (this.notificationRecipients.projectOwner) {
        recipients.push(...this.projectOwnerUsers);
      }

      recipients.push(...this.specificRecipients);

      const uniqueRecipients = Array.from(
        new Map(
          recipients.map((recipient) => [recipient._id, recipient])
        ).values()
      );

      return uniqueRecipients.filter(
        (recipient) =>
          recipient &&
          recipient._id &&
          !this.excludedRecipients.includes(recipient._id)
      );
    },
    hasRecipientsSelected() {
      return this.selectedNotificationRecipients.length > 0;
    },
  },
  methods: {
    normalizeList(value) {
      if (!value) {
        return [];
      }

      if (Array.isArray(value)) {
        return value.map((item) => String(item).trim()).filter(Boolean);
      }

      if (typeof value === 'string') {
        return value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return [];
    },
    isPowerAdminUser(user) {
      if (!user) {
        return false;
      }

      const role = String(user.role || '')
        .trim()
        .toLowerCase();
      const privil = parseInt(user.privil, 10);

      return (!isNaN(privil) && privil === 1) || role === 'power admin';
    },
    getUserPermissionKeys(user) {
      if (!user) {
        return [];
      }

      const secondaryPermissions =
        user.secondaryPermissions || user.secondaryPermission;

      const keys = this.normalizeList(user.permissions)
        .concat(this.normalizeList(user.toollist))
        .concat(this.normalizeList(secondaryPermissions?.permissions))
        .concat(this.normalizeList(secondaryPermissions?.toollist));

      return Array.from(new Set(keys));
    },
    userHasGlobalSeeAllJobsPermission(user) {
      if (this.isPowerAdminUser(user)) {
        return true;
      }

      return this.getUserPermissionKeys(user).includes('global.see_all_jobs');
    },
    normalizeSiteId(siteLike) {
      if (!siteLike) {
        return null;
      }

      if (typeof siteLike === 'string') {
        const normalized = siteLike.trim();
        return normalized || null;
      }

      if (typeof siteLike === 'object') {
        return String(siteLike._id || siteLike.id || '').trim() || null;
      }

      return String(siteLike).trim() || null;
    },
    getUserLocationIds(user) {
      if (!user) {
        return [];
      }

      const locationIds = [];
      const primaryLocationId = this.normalizeSiteId(user.location);
      if (primaryLocationId) {
        locationIds.push(primaryLocationId);
      }

      const secondaryLocations = Array.isArray(user.secondaryLocations)
        ? user.secondaryLocations
        : [];
      secondaryLocations.forEach((secondaryLocation) => {
        const secondaryLocationId = this.normalizeSiteId(secondaryLocation);
        if (secondaryLocationId) {
          locationIds.push(secondaryLocationId);
        }
      });

      return locationIds;
    },
    userBelongsToJobLocations(user) {
      const userLocationIds = this.getUserLocationIds(user);
      if (!userLocationIds.length || !this.jobLocationIds.size) {
        return false;
      }

      return userLocationIds.some((locationId) =>
        this.jobLocationIds.has(locationId)
      );
    },
    getCurrentJobSiteId() {
      if (this.currentJob?.location) {
        return this.currentJob.location;
      }

      if (this.currentJob?.executingSite) {
        return this.currentJob.executingSite;
      }

      if (
        !Array.isArray(this.currentJob?.sites) ||
        this.currentJob.sites.length === 0
      ) {
        return null;
      }

      const [firstSite] = this.currentJob.sites;
      if (typeof firstSite === 'string') {
        return firstSite;
      }

      return firstSite?._id || firstSite?.id || null;
    },
    isPrimaryLocationMatch(user, jobSiteId) {
      if (!jobSiteId) {
        return false;
      }

      const primaryLocationId = this.normalizeSiteId(user?.location);
      return primaryLocationId === jobSiteId;
    },
    isSupervisorRole(user) {
      const normalizedRoleName = String(user?.role || '')
        .toLowerCase()
        .trim()
        .replace(/[-_\s]/g, '');

      return normalizedRoleName === 'supervisor';
    },
    normalizeNotificationLogResponse(response) {
      if (Array.isArray(response?.logs)) {
        return response.logs;
      }

      if (Array.isArray(response)) {
        return response;
      }

      if (Array.isArray(response?.Items)) {
        return response.Items;
      }

      return [];
    },
    addWatcher(user) {
      if (user && !this.watchers.find((w) => this.isSameUser(w, user))) {
        this.watchers.push(user);
        this.watcherSearch = '';
        this.scheduleWatchersPersist();
      }
    },
    addCurrentUserWatcher() {
      if (!this.currentUser) {
        return;
      }

      const watcher = this.getWatcherFromUser(this.currentUser);
      if (!watcher || !watcher._id) {
        return;
      }

      if (!this.watchers.find((w) => this.hasIdentityOverlap(w, watcher))) {
        this.watchers.push(watcher);
        this.scheduleWatchersPersist();
      }
    },
    removeCurrentUserWatcher() {
      if (!this.currentUser) {
        return;
      }

      this.watchers = this.watchers.filter(
        (watcher) => !this.hasIdentityOverlap(watcher, this.currentUser)
      );
      this.scheduleWatchersPersist();
    },
    toggleCurrentUserWatcher() {
      const isCurrentUserWatcher = this.watchers.some((watcher) =>
        this.hasIdentityOverlap(watcher, this.currentUser)
      );

      if (isCurrentUserWatcher) {
        this.removeCurrentUserWatcher();
        return;
      }

      this.addCurrentUserWatcher();
    },
    removeWatcher(watcher) {
      this.watchers = this.watchers.filter(
        (w) => !this.hasIdentityOverlap(w, watcher)
      );
      this.scheduleWatchersPersist();
    },
    getWatcherIds() {
      return Array.from(
        new Set(
          this.watchers
            .map((w) => w?._id)
            .filter(Boolean)
            .map((id) => String(id).trim().toLowerCase())
        )
      );
    },
    getWatchersSignature(ids = this.getWatcherIds()) {
      return [...ids].sort().join('|');
    },
    scheduleWatchersPersist() {
      if (!this.currentJob?._id) {
        return;
      }

      const signature = this.getWatchersSignature();
      if (signature === this.watcherLastSavedSignature) {
        return;
      }

      if (this.watcherSaveTimer) {
        clearTimeout(this.watcherSaveTimer);
      }

      this.watcherSaveTimer = setTimeout(() => {
        this.persistWatchers();
      }, 700);
    },
    async persistWatchers() {
      let watcherIds = this.getWatcherIds();
      let signature = this.getWatchersSignature(watcherIds);

      if (signature === this.watcherLastSavedSignature) {
        return;
      }

      if (this.watcherPersistPromise) {
        await this.watcherPersistPromise;
        if (this.getWatchersSignature() === this.watcherLastSavedSignature) {
          return;
        }

        watcherIds = this.getWatcherIds();
        signature = this.getWatchersSignature(watcherIds);
      }

      this.watcherPersistPromise = (async () => {
        this.isSaving = true;
        try {
          await this.$store.dispatch(UPDATE_JOB_WATCHERS, {
            jobId: this.currentJob._id,
            watchers: watcherIds,
          });

          this.watcherLastSavedSignature = signature;
        } catch (error) {
          console.error('Error auto-saving watchers:', error);
          this.$buefy.snackbar.open({
            message: this.$t('error'),
            type: 'is-danger',
          });
        } finally {
          this.isSaving = false;
          this.watcherPersistPromise = null;

          if (this.getWatchersSignature() !== this.watcherLastSavedSignature) {
            this.scheduleWatchersPersist();
          }
        }
      })();

      await this.watcherPersistPromise;
    },
    async flushWatchersPersist() {
      if (this.watcherSaveTimer) {
        clearTimeout(this.watcherSaveTimer);
        this.watcherSaveTimer = null;
      }

      await this.persistWatchers();
    },
    async handleClose() {
      await this.flushWatchersPersist();
      this.$emit('close');
    },
    getUserKey(user) {
      if (!user) {
        return null;
      }

      return (
        String(user.email || user._id || user.id || '')
          .trim()
          .toLowerCase() || null
      );
    },
    getCurrentUserWatcherId() {
      const watcher = this.getWatcherFromUser(this.currentUser);
      return watcher?._id || null;
    },
    hasIdentityOverlap(left, right) {
      if (!left || !right) {
        return false;
      }

      const leftKeys = this.getUserKeys(left);
      const rightKeys = this.getUserKeys(right);

      if (!leftKeys.length || !rightKeys.length) {
        return false;
      }

      return leftKeys.some((key) => rightKeys.includes(key));
    },
    getUserKeys(user) {
      if (!user) {
        return [];
      }

      return [user.email, user._id, user.id]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());
    },
    isSameUser(left, right) {
      return this.hasIdentityOverlap(left, right);
    },
    getWatcherFromUser(user) {
      const watcherId = this.getUserKey(user);
      if (!watcherId) {
        return null;
      }

      return {
        _id: watcherId,
        fullname: user.fullname || user.name || watcherId,
        email: user.email || watcherId,
      };
    },
    async loadNotificationTemplates(force = false) {
      if (!force && this.notificationTemplates.length > 0) {
        return;
      }

      this.isLoadingTemplates = true;
      try {
        const templates = await this.$store.dispatch(
          GET_NOTIFICATION_TEMPLATES,
          {
            activeOnly: true,
            manualOnly: true,
          }
        );

        this.notificationTemplates = templates;
      } catch (error) {
        console.error('Error loading notification templates:', error);
      } finally {
        this.isLoadingTemplates = false;
      }
    },
    async loadNotificationLog(force = false) {
      if (!force && this.hasLoadedNotificationLog) {
        return;
      }
      this.isLoadingNotificationLog = true;
      try {
        const response = await this.$store.dispatch(GET_JOB_NOTIFICATION_LOG, {
          jobId: this.currentJob._id,
        });
        this.notificationLogs = this.normalizeNotificationLogResponse(response);
        this.hasLoadedNotificationLog = true;
      } catch (error) {
        console.error('Error loading notification log:', error);
        this.$buefy.snackbar.open({
          message: this.$t('error'),
          type: 'is-danger',
        });
      } finally {
        this.isLoadingNotificationLog = false;
      }
    },
    async sendNotification() {
      this.isSending = true;
      try {
        await this.$store.dispatch(SEND_JOB_NOTIFICATION, {
          jobId: this.currentJob._id,
          recipients: this.selectedNotificationRecipients,
          message: this.notificationMessage,
          templateId: this.selectedTemplateId || undefined,
        });

        this.hasLoadedNotificationLog = false;
        if (this.activeTab === 2) {
          await this.loadNotificationLog(true);
        }

        this.$buefy.snackbar.open({
          message: this.$t('notifications_modal.notification_sent'),
          type: 'is-success',
        });

        this.notificationMessage = '';
        this.notificationRecipients = {
          watchers: false,
          csm: false,
          repairEngineer: false,
          supervisor: false,
          projectOwner: false,
        };
        this.excludedRecipients = [];
        this.specificRecipients = [];
      } catch (error) {
        console.error('Error sending notification:', error);
        this.$buefy.snackbar.open({
          message: this.$t('error'),
          type: 'is-danger',
        });
      } finally {
        this.isSending = false;
      }
    },
  },
  async created() {
    if (this.users.length === 0) {
      await this.$store.dispatch(USERS_GET_USERS);
    }

    if (this.currentJob.watchers && Array.isArray(this.currentJob.watchers)) {
      this.watchers = this.users.filter((u) =>
        this.currentJob.watchers.includes(u._id)
      );
    }

    this.watcherLastSavedSignature = this.getWatchersSignature();

    await this.loadNotificationTemplates();
  },
  beforeDestroy() {
    if (this.watcherSaveTimer) {
      clearTimeout(this.watcherSaveTimer);
      this.watcherSaveTimer = null;
    }
  },
  watch: {
    activeTab(newValue) {
      if (newValue === 1) {
        this.loadNotificationTemplates();
      }
      if (newValue === 2) {
        this.loadNotificationLog();
      }
    },
  },
};
</script>

<style scoped>
.notifications-modal-card {
  --notifications-purple: #64188d;
  --notifications-purple-soft: #f3e9f9;
  --notifications-purple-border: #d8b7e8;
}

:deep(.modal.notifications-modal-wrapper) {
  align-items: center;
  justify-content: center;
}

.notifications-modal-card {
  width: min(1250px, calc(100vw - 4rem));
  max-width: calc(100vw - 4rem);
  max-height: calc(100vh - 4rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-card-body {
  flex: 1 1 auto;
  overflow-y: auto;
}

.notifications-modal-head {
  display: flex;
  align-items: center;
}

.notifications-modal-close {
  margin-left: auto;
}

.notifications-tabs :deep(.tabs) {
  margin-bottom: 0;
}

.notifications-tabs :deep(.tabs li a) {
  font-weight: 700;
  color: #4a4a4a;
  border-bottom-width: 3px;
}

.notifications-tabs :deep(.tabs li.is-active a) {
  color: var(--notifications-purple);
  border-bottom-color: var(--notifications-purple);
  background: rgba(100, 24, 141, 0.08);
}

.notifications-tabs :deep(.tabs li a:hover) {
  color: var(--notifications-purple);
}

.notifications-tabs :deep(.tabs li:focus-within a) {
  color: var(--notifications-purple);
}

.modal-card-foot {
  gap: 10px;
}

.notifications-btn-primary {
  background-color: var(--notifications-purple) !important;
  border-color: var(--notifications-purple) !important;
  color: #fff !important;
}

.notifications-btn-primary:hover,
.notifications-btn-primary:focus {
  filter: brightness(0.95);
}

.notifications-btn-close {
  border-color: var(--notifications-purple-border) !important;
  color: var(--notifications-purple) !important;
}

.notifications-btn-close:hover,
.notifications-btn-close:focus {
  background: var(--notifications-purple-soft) !important;
}

.modal-card-body :deep(.checkbox input[type='checkbox']:checked + .check) {
  border-color: var(--notifications-purple);
  background: var(--notifications-purple);
}

.modal-card-body
  :deep(.checkbox input[type='checkbox']:checked + .check::after) {
  border-color: #fff !important;
}

.modal-card-body
  :deep(.checkbox input[type='checkbox']:checked:disabled + .check::after) {
  border-color: #fff !important;
  opacity: 0.95;
}

.modal-card-body :deep(.input:focus),
.modal-card-body :deep(.textarea:focus),
.modal-card-body :deep(.select select:focus),
.modal-card-body :deep(.autocomplete .input:focus) {
  border-color: var(--notifications-purple);
  box-shadow: 0 0 0 0.125em rgba(100, 24, 141, 0.2);
}

@media screen and (max-width: 1024px) {
  .notifications-modal-card {
    width: 100vw;
    max-width: none;
    height: 100vh;
    max-height: 100vh;
    margin: 0;
    border-radius: 0;
  }

  .notifications-modal-card .modal-card-body {
    -webkit-overflow-scrolling: touch;
  }
}
</style>

<style>
@media screen and (min-width: 1025px) {
  .modal.notifications-modal-wrapper,
  .notifications-modal-wrapper {
    position: fixed !important;
    inset: 0 !important;
    display: grid !important;
    place-items: center !important;
    padding: 1rem !important;
  }

  .modal.notifications-modal-wrapper .modal-card.notifications-modal-card,
  .notifications-modal-wrapper .modal-card.notifications-modal-card {
    margin: 0 !important;
    max-height: calc(100vh - 6rem) !important;
    align-self: center !important;
  }

  .modal.notifications-modal-wrapper .animation-content,
  .modal.notifications-modal-wrapper .modal-content,
  .notifications-modal-wrapper .animation-content,
  .notifications-modal-wrapper .modal-content {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    max-width: none !important;
    height: 100% !important;
    margin: 0 !important;
  }
}
</style>
